/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import { useNavigate } from "react-router-dom"; // Import useNavigate hook for navigation between routes
import React, { useState } from "react"; // Import React and useState hook for state management
import {useDispatch, useSelector} from "react-redux"; // Import useSelector hook to access Redux state
import { TABLES } from "../../../../../Apis/Tables"; // Import table constants
import CustomChip from "../../../../../components/CustomChip"; // Import custom chip component for status display
import { statusBorderColor, statusColor, statusFontColor } from "../../../../../enums/statusColorEnum"; // Import status color utilities
import { status } from "../../../../../enums/statusEnum"; // Import status enumeration
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile"; // Import dropdown menu for action buttons
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem"; // Import dropdown menu item component
import { permissionObj } from "../../../../../Helpers/permission"; // Import permission object for checking user permissions
import routes from "../../../../../Routes/routes"; // Import app routes
import TableFunction from "../../../../../components/table/TableFunction"; // Import TableFunction for table logic
import {
    DeleteIconsOptions,
    UpdateIconsOptions
} from "../../../../../components/buttons/IconButtons";
import {SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi"; // Import icon components for action buttons

// Main component to display cafeteria data in a table format
const CafeteriaTableApp = ({
       searchElement,
       selectedList,
       setSelectedList,
       modified,
       setModified,
       setBodyForExport,
       setIsAllChecked,
       onDeleteItem,
       option
   }) => {
    const dispatch = useDispatch(); // Initialize Redux dispatch
    // Access development mode from Redux state
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    // Access user permissions from Redux state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const navigate = useNavigate(); // Initialize navigation hook

    // Local state variables
    const tableId = "tbl_cta_cafeteria"; // Table ID for DOM usage
    const searchId = "txt_cta_search_cafeteria"; // Search input ID for DOM usage
    const [results, setResults] = useState(null); // Store search results
    const [expandedRowId, setExpandedRowId] = useState(null); // Store ID of the currently expanded row

    // Define the table columns
    const columns = [
        { field: 'name', label: 'name', className: "w-40vw" },
        { field: 'code', label: 'code', className: "w-10vw" },
        { field: 'capacity', label: 'capacity', className: "w-5vw" },
        {
            field: 'statusId',
            label: 'status',
            className: "w-10vw",
            render: (value) => {
                // Render status chip using CustomChip component
                return (
                    <div className={"d-flex justify-content-center"}>
                        <CustomChip
                            background={statusColor[value]}
                            fontColor={statusFontColor[value]}
                            borderColor={statusBorderColor[value]}
                            legend={t(status[value]) || '-'}
                            variant={'large'}
                        />
                    </div>
                );
            }
        }
    ];

    // Function to generate action buttons dropdown menu for each row
    const getActionsButtons = (item, index) => {
        return (
            <>
                <DropDownMenuProfile
                    openMenu={openMenu}
                    handleMenuToggle={handleMenuToggle}
                    menuId={`menu-${item.id}`} // Unique ID for each menu
                >
                    {/* Show update option if in development mode or user has update permission */}
                    {(DEV || permission?.includes(permissionObj?.WEB_FOODIE_CAFETERIA_UPDATE)) && (
                        <DropDownMenuItem
                            id={"btn_cta_update_" + item.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('update')}
                            icon={<UpdateIconsOptions />}
                            click={() => {
                                // Navigate to the cafeteria update route with item ID
                                navigate(routes.foodieCafeteria.update, {
                                    state: { state: item.id }
                                });
                            }}
                        />
                    )}
                    {/* Show delete option if in development mode or user has delete permission */}
                    {(DEV || permission?.includes(permissionObj?.WEB_FOODIE_CAFETERIA_DELETE)) && (
                        <DropDownMenuItem
                            id={"btn_cta_delete_" + item.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('delete')}
                            icon={<DeleteIconsOptions />}
                            click={() => {
                                onDeleteItem(item); // Call delete handler
                            }}
                            fontColor={'font-color-error-600'}
                            iconColor={"icon-color-error-code-600"}
                        />
                    )}
                </DropDownMenuProfile>
            </>
        );
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, key: searchElement, model: option });
        setBodyForExport(body); // Save request body for export
        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setResults(data); // Update results if successful
                }
            }
        );
    }

    // Use TableFunction hook to handle table logic (checkbox, pagination, etc.)
    const {
        t,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'cafeterias_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    // Function to handle row expansion
    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            setExpandedRowId(row?.id); // Toggle expanded row ID
        }
    };

    // Return all necessary props and handlers for rendering the table
    return {
        t,
        DEV,
        permission,
        navigate,
        tableId,
        searchId,
        pagination,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        results,
        setResults,
        page,
        rowsPerPage,
        columns,
        handleRowExpand
    };
};

export default CafeteriaTableApp; // Export the component for use in other parts of the app
